import { Alert, App, Button } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import { FieldSection } from '@/Ship/Components/EntityCard'
import { classNames } from '@/Ship/Helpers/classNames'

import { UserPasswordManager } from '../../Stores/UserPasswordManager'

import type { UserFormContextLike } from '../../Contracts/IUser'

import styles from '../UserCard.module.css'

const FIELDS = {
    plainPassword: {
        label: 'Пароль',
        type: 'password',
        isRequired: true,
        inputProps: { autoComplete: 'new-password' },
    },
    plainPasswordConfirm: {
        label: 'Повторити пароль',
        type: 'password',
        isRequired: true,
        inputProps: { autoComplete: 'new-password' },
    },
}

interface SubmitArgs {
    values: Record<string, unknown>
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '')

/**
 * Вкладка «Змінити пароль» (легасі `UserChangePassword.jsx`).
 *
 * Відмінності від легасі (`SPEC-005` §7.2): паролі порівнюються й закороткий
 * не відправляється (D-28, ВП-3), а форма очищається лише після успіху — у
 * легасі вона спорожнювалася ще до відповіді сервера, і помилку не було з
 * чим виправляти.
 */
export const UserPasswordTab = observer(({ formContext }: { formContext: UserFormContextLike }) => {
    const { notification } = App.useApp()
    const [manager] = useState(() => new UserPasswordManager())
    const [formKey, setFormKey] = useState(0)
    const uuid = typeof formContext.values?.uuid === 'string' ? formContext.values.uuid : undefined

    const onSubmit = async ({ values }: SubmitArgs): Promise<void> => {
        if (!uuid) return

        const changed = await manager.submit(uuid, text(values['plainPassword']), text(values['plainPasswordConfirm']))

        if (!changed) return

        // Нова форма замість очищення старої: ядровий `Form` тримає контекст
        // на весь час життя компонента, і порожні значення надійніше дає новий
        setFormKey((key) => key + 1)
        notification.success({ title: 'Пароль успішно оновлено.', placement: 'topRight' })
    }

    return (
        <FieldSection title="Змінити пароль">
            <div className={classNames(styles['tabForm'])}>
                <Form key={formKey} fields={FIELDS} autoComplete="off" onSubmit={onSubmit}>
                    <FormField name="plainPassword" />
                    <FormField name="plainPasswordConfirm" />

                    {manager.error ? <Alert type="error" showIcon title={manager.error} /> : null}

                    <div className={classNames(styles['formButtons'])}>
                        <Button type="primary" htmlType="submit" loading={manager.isLoading}>
                            Змінити
                        </Button>
                    </div>
                </Form>
            </div>
        </FieldSection>
    )
})
